import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  api,
  type AppointmentWireItem,
  type CapacitySignalsWire,
  type SchedulingBlockWire,
  type SchedulingResourceWire,
  type SchedulingServiceWire,
  type SchedulingSlotWire,
  type SchedulingWorkingHoursWire,
  type WaitlistEntryWire,
  type WaitlistPriorityTierWire,
  type WaitlistSignalsWire,
} from '../lib/api-client';

/**
 * Hooks da agenda NATIVA (Frente B Bloco II — gated `scheduling-native`).
 *
 * READ-only: catálogo de recursos/serviços + disponibilidade (livre/ocupado).
 * Alimentam o slot-picker + a visão de disponibilidade do operador. Seguindo
 * `feedback_gatear_fetch_nao_so_render`, a QUERY é gateada por `enabled` (não só o
 * render) — tenants sem agenda nativa não disparam fetch (o endpoint é 403 gated).
 */
export function useSchedulingResources(
  enabled = true,
): UseQueryResult<{ data: SchedulingResourceWire[] }, Error> {
  return useQuery({
    queryKey: ['scheduling', 'resources'],
    queryFn: () => api.getSchedulingResources(),
    enabled,
    staleTime: 5 * 60_000, // catálogo é estático — cache mais longo
  });
}

export function useSchedulingServices(
  enabled = true,
): UseQueryResult<{ data: SchedulingServiceWire[] }, Error> {
  return useQuery({
    queryKey: ['scheduling', 'services'],
    queryFn: () => api.getSchedulingServices(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * useSchedulingAvailability — disponibilidade (slots livres) p/ um serviço numa
 * janela. `enabled` deve incluir a presença do serviceOfferingId (não buscar antes
 * de o operador escolher o serviço). A ocupação muda a cada booking → staleTime curto.
 */
export function useSchedulingAvailability(
  params: { serviceOfferingId: string; from: string; to: string; resourceIds?: string[]; limit?: number },
  enabled = true,
): UseQueryResult<{ data: SchedulingSlotWire[] }, Error> {
  return useQuery({
    queryKey: ['scheduling', 'availability', params],
    queryFn: () => api.getSchedulingAvailability(params),
    enabled: enabled && params.serviceOfferingId !== '',
    staleTime: 15_000,
  });
}

/**
 * useCreateNativeBooking — booking nativo pelo operador (slot-picker), sob a guarda
 * de slot livre. Em sucesso invalida `['appointments']` (a grade) + `['scheduling']`
 * (a disponibilidade — o slot deixou de estar livre). 409 = horário tomado (reconduz).
 */
export function useCreateNativeBooking(): UseMutationResult<
  AppointmentWireItem,
  Error,
  Parameters<typeof api.createNativeBooking>[0]
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof api.createNativeBooking>[0]) =>
      api.createNativeBooking(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['appointments'] });
      void qc.invalidateQueries({ queryKey: ['scheduling'] });
    },
  });
}

/**
 * Hooks de GESTÃO DE AGENDA (Frente B Sessão 1 — fechar/reabrir período por recurso).
 * Gated `scheduling-native`. A lista é por-recurso (o endpoint exige resourceId).
 */
export function useSchedulingBlocks(
  resourceId: string,
  enabled = true,
): UseQueryResult<{ data: SchedulingBlockWire[] }, Error> {
  return useQuery({
    queryKey: ['scheduling', 'blocks', resourceId],
    queryFn: () => api.getSchedulingBlocks({ resourceId }),
    enabled: enabled && resourceId !== '',
    staleTime: 30_000,
  });
}

/**
 * Blocos CROSS-RESOURCE numa janela (bloqueios inline na grade recurso×tempo — fecha o DRIVER
 * Sessão 1). Sem `resourceId` → todos os recursos do tenant no range. Gateia o FETCH por
 * `enabled` (a cap `scheduling-native` + a view de dia) — [[feedback_gatear_fetch_nao_so_render]].
 */
export function useSchedulingBlocksRange(
  fromIso: string,
  toIso: string,
  enabled = true,
): UseQueryResult<{ data: SchedulingBlockWire[] }, Error> {
  return useQuery({
    queryKey: ['scheduling', 'blocks', 'range', fromIso, toIso],
    queryFn: () => api.getSchedulingBlocks({ from: fromIso, to: toIso }),
    enabled,
    staleTime: 30_000,
  });
}

/** Fechar (createBlock) / reabrir (deleteBlock) período — invalidam blocks + disponibilidade. */
export function useBlockActions(): {
  create: UseMutationResult<
    { data: SchedulingBlockWire },
    Error,
    { resourceId: string; startsAt: string; endsAt: string; reason?: string }
  >;
  remove: UseMutationResult<{ data: { deleted: true } }, Error, { id: string }>;
} {
  const qc = useQueryClient();
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['scheduling'] });
  };
  return {
    create: useMutation({
      mutationFn: (p: { resourceId: string; startsAt: string; endsAt: string; reason?: string }) =>
        api.createSchedulingBlock(p),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (p: { id: string }) => api.deleteSchedulingBlock(p.id),
      onSuccess: invalidate,
    }),
  };
}

/**
 * Hooks de GESTÃO DO CATÁLOGO DE RECURSOS (Plano 2 setup — gated `scheduling-native`).
 * O operador cadastra/edita/aposenta as unidades-calendário do negócio (cadeira/sala/
 * profissional). A LEITURA é `useSchedulingResources`; aqui as mutações. PATCH/DELETE
 * carregam a `version` (If-Match · ADR-071). Em sucesso invalidam `['scheduling']`
 * (o catálogo + a disponibilidade derivada). É config (rara) → sem cache especial.
 */
export function useResourceActions(): {
  create: UseMutationResult<
    { data: SchedulingResourceWire },
    Error,
    { name: string; resourceType: SchedulingResourceWire['resourceType']; capacity?: number; timezone?: string; userId?: string }
  >;
  update: UseMutationResult<
    { data: SchedulingResourceWire },
    Error,
    {
      id: string;
      version: number;
      name?: string;
      resourceType?: SchedulingResourceWire['resourceType'];
      capacity?: number;
      timezone?: string;
      // `null` desvincula o profissional (User) do recurso.
      userId?: string | null;
    }
  >;
  deactivate: UseMutationResult<{ data: { deactivated: true } }, Error, { id: string; version: number }>;
} {
  const qc = useQueryClient();
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['scheduling'] });
  };
  return {
    create: useMutation({
      mutationFn: (p: Parameters<typeof api.createSchedulingResource>[0]) =>
        api.createSchedulingResource(p),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, version, ...body }: { id: string; version: number } & Parameters<typeof api.updateSchedulingResource>[2]) =>
        api.updateSchedulingResource(id, version, body),
      onSuccess: invalidate,
    }),
    deactivate: useMutation({
      mutationFn: (p: { id: string; version: number }) =>
        api.deactivateSchedulingResource(p.id, p.version),
      onSuccess: invalidate,
    }),
  };
}

/**
 * Hooks de GESTÃO DO CATÁLOGO DE SERVIÇOS (Plano 2 setup — gated `scheduling-native`).
 * O operador cadastra/edita/aposenta os serviços do negócio (duração/buffers/recursos
 * exigidos/lead-time/fila). A LEITURA é `useSchedulingServices`. Em sucesso invalidam
 * `['scheduling']`. PATCH/DELETE carregam a `version` (If-Match · ADR-071).
 */
export function useServiceActions(): {
  create: UseMutationResult<
    { data: SchedulingServiceWire },
    Error,
    Parameters<typeof api.createSchedulingService>[0]
  >;
  update: UseMutationResult<
    { data: SchedulingServiceWire },
    Error,
    { id: string; version: number } & Parameters<typeof api.updateSchedulingService>[2]
  >;
  deactivate: UseMutationResult<{ data: { deactivated: true } }, Error, { id: string; version: number }>;
} {
  const qc = useQueryClient();
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['scheduling'] });
  };
  return {
    create: useMutation({
      mutationFn: (p: Parameters<typeof api.createSchedulingService>[0]) =>
        api.createSchedulingService(p),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, version, ...body }: { id: string; version: number } & Parameters<typeof api.updateSchedulingService>[2]) =>
        api.updateSchedulingService(id, version, body),
      onSuccess: invalidate,
    }),
    deactivate: useMutation({
      mutationFn: (p: { id: string; version: number }) =>
        api.deactivateSchedulingService(p.id, p.version),
      onSuccess: invalidate,
    }),
  };
}

/**
 * Hooks de HORÁRIO de funcionamento (Plano 2 setup — gated `scheduling-native`).
 * Janela recorrente por recurso (`WorkingHours`). A lista é por-recurso (o endpoint exige
 * resourceId → só busca com um recurso selecionado). Sem update: editar = remover + recriar.
 */
export function useWorkingHours(
  resourceId: string,
  enabled = true,
): UseQueryResult<{ data: SchedulingWorkingHoursWire[] }, Error> {
  return useQuery({
    queryKey: ['scheduling', 'working-hours', resourceId],
    queryFn: () => api.getSchedulingWorkingHours(resourceId),
    enabled: enabled && resourceId !== '',
    staleTime: 30_000,
  });
}

/** Adicionar/remover janela de trabalho — invalidam horário + a disponibilidade derivada. */
export function useWorkingHoursActions(): {
  create: UseMutationResult<
    { data: SchedulingWorkingHoursWire },
    Error,
    Parameters<typeof api.createSchedulingWorkingHours>[0]
  >;
  remove: UseMutationResult<{ data: { deleted: true } }, Error, { id: string }>;
} {
  const qc = useQueryClient();
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['scheduling'] });
  };
  return {
    create: useMutation({
      mutationFn: (p: Parameters<typeof api.createSchedulingWorkingHours>[0]) =>
        api.createSchedulingWorkingHours(p),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (p: { id: string }) => api.deleteSchedulingWorkingHours(p.id),
      onSuccess: invalidate,
    }),
  };
}

/**
 * Hooks da FILA DE ESPERA (Frente B Bloco IV PR6 — gated `scheduling-waitlist`).
 *
 * Controle do operador sobre a fila: listar (ordenada), aprovar/recusar `pending_approval`,
 * reordenar via prioridade, liberar manual. Seguindo `feedback_gatear_fetch_nao_so_render`,
 * a QUERY é gateada por `enabled` (a cap) — tenants sem fila não disparam fetch (403 gated).
 */
export function useWaitlist(
  params: { serviceOfferingId?: string; resourceId?: string },
  enabled = true,
): UseQueryResult<{ data: WaitlistEntryWire[] }, Error> {
  return useQuery({
    queryKey: ['scheduling', 'waitlist', params],
    queryFn: () => api.getWaitlist(params),
    enabled,
    staleTime: 15_000,
  });
}

/** Aprova/recusa/reordena/libera-manual — todas invalidam a fila + a disponibilidade. */
export function useWaitlistActions(): {
  approve: UseMutationResult<{ data: WaitlistEntryWire }, Error, { id: string; version: number }>;
  reject: UseMutationResult<{ data: WaitlistEntryWire }, Error, { id: string; version: number }>;
  reprioritize: UseMutationResult<
    { data: WaitlistEntryWire },
    Error,
    { id: string; version: number; priorityTier: WaitlistPriorityTierWire }
  >;
  promote: UseMutationResult<
    { data: { booked: boolean; appointmentId: string | null; offeredCount: number } },
    Error,
    { id: string; resourceId: string; startsAt: string; durationMinutes?: number }
  >;
} {
  const qc = useQueryClient();
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['scheduling'] });
    void qc.invalidateQueries({ queryKey: ['appointments'] });
  };
  return {
    approve: useMutation({
      mutationFn: (p: { id: string; version: number }) => api.approveWaitlistEntry(p.id, p.version),
      onSuccess: invalidate,
    }),
    reject: useMutation({
      mutationFn: (p: { id: string; version: number }) => api.rejectWaitlistEntry(p.id, p.version),
      onSuccess: invalidate,
    }),
    reprioritize: useMutation({
      mutationFn: (p: { id: string; version: number; priorityTier: WaitlistPriorityTierWire }) =>
        api.updateWaitlistPriority(p.id, p.version, p.priorityTier),
      onSuccess: invalidate,
    }),
    promote: useMutation({
      mutationFn: (p: { id: string; resourceId: string; startsAt: string; durationMinutes?: number }) =>
        api.promoteWaitlistEntry(p.id, {
          resourceId: p.resourceId,
          startsAt: p.startsAt,
          ...(p.durationMinutes !== undefined && { durationMinutes: p.durationMinutes }),
        }),
      onSuccess: invalidate,
    }),
  };
}

/**
 * Sinais de FILA/CAPACIDADE do painel do Supervisor (Frente B — reconciliação, Sessão 5).
 *
 * Agregações read-only. `feedback_gatear_fetch_nao_so_render`: a QUERY é gateada por
 * `enabled` (a cap) — fila por `scheduling-waitlist`, capacidade por `scheduling-native`.
 * Fora da cap, nenhum fetch dispara (o 403 gated não vaza em toast).
 */
export function useWaitlistSignals(
  enabled = true,
  windowDays?: number,
): UseQueryResult<{ data: WaitlistSignalsWire }, Error> {
  return useQuery({
    queryKey: ['scheduling', 'waitlist-signals', windowDays ?? null],
    queryFn: () => api.getWaitlistSignals(windowDays !== undefined ? { windowDays } : undefined),
    enabled,
    staleTime: 30_000,
  });
}

export function useCapacitySignals(
  enabled = true,
  horizonDays?: number,
): UseQueryResult<{ data: CapacitySignalsWire }, Error> {
  return useQuery({
    queryKey: ['scheduling', 'capacity-signals', horizonDays ?? null],
    queryFn: () =>
      api.getCapacitySignals(horizonDays !== undefined ? { horizonDays } : undefined),
    enabled,
    staleTime: 30_000,
  });
}
